/**
 * Top-bar dealer-group switcher. Operators get a dropdown (All Groups + each
 * group); dealership users get a read-only label of their own group. Styled for
 * the dark brand top bar — glass tint matching the logout button (TopBar.tsx).
 */
import React from "react"
import { Building2, ChevronDown } from "lucide-react"

import { useDealerGroup } from "../context/DealerGroupContext"
import { THEME } from "../config/theme"

const ALL_GROUPS_VALUE = "all"

interface Props {
  // On mobile the top bar is tight — cap the control's width harder.
  compact?: boolean
}

const DealerGroupSwitcher: React.FC<Props> = ({ compact = false }) => {
  const { activeGroupId, activeGroupName, isOperator, groups, setActiveGroup } = useDealerGroup()

  // Dealership user: locked to their own group — show a read-only label.
  if (!isOperator) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 12px",
          fontSize: "13px",
          fontWeight: 500,
          color: THEME.colors.neutral.dark.textPrimary,
          background: "rgba(255, 255, 255, 0.06)",
          border: "0.5px solid rgba(255, 255, 255, 0.12)",
          borderRadius: THEME.borderRadius.md,
          fontFamily: THEME.fonts.primary,
          whiteSpace: "nowrap",
        }}
        title={activeGroupName}
      >
        <Building2 size={13} strokeWidth={1.75} />
        {activeGroupName}
      </span>
    )
  }

  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <Building2
        size={13}
        strokeWidth={1.75}
        style={{
          position: "absolute",
          left: 10,
          color: THEME.colors.neutral.dark.textSecondary,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      />
      <select
        aria-label="Active dealer group"
        value={activeGroupId === ALL_GROUPS_VALUE ? ALL_GROUPS_VALUE : String(activeGroupId)}
        onChange={(e) =>
          setActiveGroup(e.target.value === ALL_GROUPS_VALUE ? "all" : Number(e.target.value))
        }
        style={{
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          background: "rgba(255, 255, 255, 0.08)",
          color: THEME.colors.neutral.dark.textPrimary,
          border: "0.5px solid rgba(255, 255, 255, 0.18)",
          borderRadius: THEME.borderRadius.md,
          padding: "5px 26px 5px 28px",
          fontSize: "13px",
          fontWeight: 500,
          fontFamily: THEME.fonts.primary,
          cursor: "pointer",
          outline: "none",
          maxWidth: compact ? "118px" : "220px",
          textOverflow: "ellipsis",
        }}
      >
        {/* Options render in the OS dropdown — force light-surface colors so
            they stay readable instead of inheriting the dark bar's text. */}
        <option value={ALL_GROUPS_VALUE} style={OPTION_STYLE}>
          All Groups
        </option>
        {groups.map((g) => (
          <option key={g.id} value={g.id} style={OPTION_STYLE}>
            {g.name}
          </option>
        ))}
      </select>
      <ChevronDown
        size={14}
        strokeWidth={2}
        style={{
          position: "absolute",
          right: 8,
          color: THEME.colors.neutral.dark.textSecondary,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      />
    </div>
  )
}

const OPTION_STYLE: React.CSSProperties = {
  color: THEME.colors.neutral.light.textPrimary,
  background: THEME.colors.neutral.light.surface,
}

export default DealerGroupSwitcher
